import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

export interface IEsignPermissions {
  /** Студенческие договоры: чтение — Admin, AM, Sales, FinanceManager. */
  canReadStudent: boolean;
  /**
   * Студенческие договоры: создать, отправить, раскрыть ссылку, отменить,
   * повторить зачисление — Admin, AM, Sales. FinanceManager только читает.
   */
  canWriteStudent: boolean;
  /**
   * Документы преподавателей: всё — Admin, AM, Sales, HRManager.
   *
   * 🔴 НЕ `PANDADOC_ROLES`: там нет HRManager, а рамочный договор оформляет
   * именно HR.
   */
  canTeacherDocs: boolean;
  /** Виден ли раздел «Договоры» вообще (читает хоть что-то). */
  canOpenPage: boolean;
  /**
   * Admin: смена продавца договора (`reassignContractSeller`, ТЗ 0078) —
   * мутация закрыта на бэкенде той же ролью.
   */
  isAdmin: boolean;
}

/**
 * Роли, которые видят раздел «Договоры». Зеркало `ESIGN_READ_ROLES` бэкенда
 * (`esign/schema/schema.py`) — меню, маршрут и палитра берут список отсюда,
 * чтобы не разъехаться с экраном.
 */
export const ESIGN_PAGE_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER,
  UserRole.HRMANAGER,
];

/** Кто создаёт студенческий договор — `ESIGN_STUDENT_WRITE_ROLES`. */
export const ESIGN_STUDENT_WRITE_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
];

/**
 * ТЗ 0080: раздел «Короткие ссылки» — зеркало `SHORTLINK_ROLES` бэкенда
 * (`shortlinks/schema.py`): те, кто отправляет договоры и рассылки.
 *
 * Состав совпадает с `ESIGN_STUDENT_WRITE_ROLES`, но объявлен отдельно
 * намеренно: сократитель от подписи не зависит, и правило одного не обязано
 * двигать другое. Меню, маршрут и палитра берут список ТОЛЬКО отсюда.
 */
export const SHORT_LINK_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
];

/**
 * Кто читает студенческие договоры — `ESIGN_STUDENT_READ_ROLES` бэкенда. Им
 * же открыты справочник шаблонов и аналитика (ТЗ 0078): маршрут, пункт
 * меню и палитра берут список отсюда.
 */
export const ESIGN_STUDENT_READ_ROLES: UserRole[] = [
  ...ESIGN_STUDENT_WRITE_ROLES,
  UserRole.FINANCIER,
];

const ESIGN_TEACHER_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
];

const hasAny = (roles: UserRole[], allowed: UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

/**
 * ТЗ 0076 §3.3 — зеркало ролевых списков бэкенда.
 *
 * Скрытие кнопки — это UX, а не защита: авторитет по каждой мутации остаётся
 * за бэкендом. Но `canRead*` нужен ещё и чтобы НЕ отправлять запрос с чужой
 * карточки: запрос без прав отклоняет промис Apollo и рисует ошибку поверх
 * постороннего экрана.
 */
export const useEsignPermissions = (): IEsignPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];

  const canReadStudent = hasAny(roles, ESIGN_STUDENT_READ_ROLES);
  const canTeacherDocs = hasAny(roles, ESIGN_TEACHER_ROLES);

  return {
    canReadStudent,
    canWriteStudent: hasAny(roles, ESIGN_STUDENT_WRITE_ROLES),
    canTeacherDocs,
    canOpenPage: canReadStudent || canTeacherDocs,
    isAdmin: roles.includes(UserRole.ADMIN),
  };
};
